import { useState, useEffect, useRef } from 'react'
import { MapContainer, TileLayer, Marker, Polyline, useMapEvents, useMap } from 'react-leaflet'
import L from 'leaflet'
import { fetchWithAuth } from '../utils/api'
import { Site, Area } from '../types'
import BaseMapSwitcher from './BaseMapSwitcher'
import { useBaseMap } from '../context/BaseMapContext'
import { BASEMAPS } from '../utils/basemaps'

// Fix Leaflet marker icon paths
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/images/marker-icon-2x.png',
  iconUrl: '/images/marker-icon.png',
  shadowUrl: '/images/marker-shadow.png',
})

// Utility function to check if a point is inside a polygon
function isPointInPolygon(point: [number, number], polygon: [number, number][]): boolean {
  const [lng, lat] = point
  let inside = false
  
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = [polygon[i][1], polygon[i][0]] // Convert to lat, lng for calculation
    const [xj, yj] = [polygon[j][1], polygon[j][0]]
    
    if (((yi > lat) !== (yj > lat)) && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi)) {
      inside = !inside
    }
  }
  
  return inside
}

// Check point in GeoJSON MultiPolygon (coordinates are [lng,lat])
function isPointInMultiPolygon(point: [number, number], multi: any): boolean {
  if (!multi || multi.type !== 'MultiPolygon' || !Array.isArray(multi.coordinates)) return false
  for (const poly of multi.coordinates) {
    const ring = poly && poly[0]
    if (!Array.isArray(ring)) continue
    const coordsLatLng: [number, number][] = ring.map((p: [number, number]) => [p[1], p[0]])
    if (isPointInPolygon(point, coordsLatLng)) return true
  }
  return false
}

function centroidOfMultiPolygon(multi: any): [number, number] | null {
  if (!multi || multi.type !== 'MultiPolygon' || !Array.isArray(multi.coordinates)) return null
  let sumLat = 0, sumLng = 0, count = 0
  for (const poly of multi.coordinates) {
    const ring = poly && poly[0]
    if (!Array.isArray(ring) || ring.length === 0) continue
    for (const p of ring) {
      sumLat += p[1]
      sumLng += p[0]
      count++
    }
  }
  if (count === 0) return null
  return [sumLat / count, sumLng / count]
}

interface WildlifeObservationProps {
  onClose: () => void
  onSuccess: () => void
}

interface WildlifeType {
  name: string
  icon: string
  color: string
}

// Component to handle map view updates when position changes
function MapViewController({ position }: { position: [number, number] | null }) {
  const map = useMap()

  useEffect(() => {
    if (position) {
      const latLng = [position[1], position[0]] as [number, number] // Convert [lng, lat] to [lat, lng]
      map.setView(latLng, 15)
    }
  }, [position, map])

  return null
}

// Location picker with GPS support
function LocationPicker({ 
  position, 
  onLocationChange, 
  onUseCurrentLocation,
  loading = false,
  children,
  movement,
  onMovementChange,
  directionDeg,
  locked,
  centerLatLng,
  centerTick,
}: { 
  position: [number, number] | null
  onLocationChange: (coords: [number, number] | null) => void
  onUseCurrentLocation: () => void
  loading?: boolean
  children?: React.ReactNode
  movement: string
  onMovementChange: (dir: string) => void
  directionDeg: number | null
  locked: boolean
  centerLatLng?: [number, number] | null
  centerTick?: number
}) {
  const { baseId, setBaseId } = useBaseMap()
  const baseCfg = BASEMAPS[baseId]
  const mapRef = useRef<any>(null)
  const MapClickHandler = () => {
    useMapEvents({
      click(e) {
        if (locked) return
        const { lat, lng } = e.latlng
        onLocationChange([lng, lat]) // Store as [lng, lat]
      }
    })
    return null
  }

  // Enable/disable map interactions based on locked state
  function InteractionController() {
    const map = useMap()
    useEffect(() => {
      // Allow dragging in both modes to avoid "snap back" feeling after pan.
      map.dragging.enable();
      if (locked) {
        map.scrollWheelZoom.disable();
        map.doubleClickZoom.disable();
        map.boxZoom.disable();
        map.keyboard.disable();
        map.touchZoom.disable();
      } else {
        map.scrollWheelZoom.enable();
        map.doubleClickZoom.enable();
        map.boxZoom.enable();
        map.keyboard.enable();
        map.touchZoom.enable();
      }
      const onDragStart = () => { /* noop */ }
      const onDragEnd = () => {
        try {
          const c = map.getCenter();
          ;(window as any).__wildlifeLastDrag = { lat: c.lat, lng: c.lng, ts: Date.now() }
        } catch {}
      }
      const onMoveEnd = () => {
        try {
          const c = map.getCenter();
          const ts = Date.now()
          const w: any = window as any
          const last = w.__wildlifeLastDrag
          const init = w.__wildlifeInitCenter
          if (last && init && ts - last.ts < 1000) {
            const dToInit = Math.hypot(c.lat - init.lat, c.lng - init.lng)
            const dToLast = Math.hypot(c.lat - last.lat, c.lng - last.lng)
            if (dToInit < 0.0005 && dToLast > 0.0005) {
              map.panTo([last.lat, last.lng])
            }
          }
        } catch {}
      }
      map.on('dragstart', onDragStart)
      map.on('dragend', onDragEnd)
      map.on('moveend', onMoveEnd)
      return () => {
        map.off('dragstart', onDragStart)
        map.off('dragend', onDragEnd)
        map.off('moveend', onMoveEnd)
      }
    }, [locked, map])
    return null
  }

  // Center the map only when an explicit center trigger occurs
  function CenterController() {
    const map = useMap()
    useEffect(() => {
      if (!centerLatLng || !centerTick) return
      const w: any = (typeof window !== 'undefined') ? window : {}
      const applied = w.__wildlifeCenterApplied || { tick: 0 }
      if (applied.tick === centerTick) { return }
      map.setView(centerLatLng, Math.max(map.getZoom(), 15))
      w.__wildlifeCenterApplied = { tick: centerTick, lat: centerLatLng[0], lng: centerLatLng[1] }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [centerTick])
    return null
  }

  // Use a static initial center to avoid prop-driven recentering; rely on setView imperatively
  const initialCenter: [number, number] = [59.3293, 18.0686]

  // Compute a short arrow line (and head) from current position in selected direction
  const computeArrow = () => {
    if (!position || directionDeg == null) return null
    const [lng, lat] = position
    const start: [number, number] = [lat, lng]
    const end = destinationPointLatLng(lat, lng, 120, directionDeg) // 120 m line
    // Arrowhead should point back from the tip (end) toward the shaft at ±25°
    const headLeft = destinationPointLatLng(end[0], end[1], 14, (directionDeg + 180 - 25))
    const headRight = destinationPointLatLng(end[0], end[1], 14, (directionDeg + 180 + 25))
    return { start, end, headLeft, headRight }
  }
  
  function destinationPointLatLng(lat: number, lon: number, distMeters: number, bearingDeg: number): [number, number] {
    const R = 6371000
    const δ = distMeters / R
    const θ = (bearingDeg * Math.PI) / 180
    const φ1 = (lat * Math.PI) / 180
    const λ1 = (lon * Math.PI) / 180
    const sinφ1 = Math.sin(φ1), cosφ1 = Math.cos(φ1)
    const sinδ = Math.sin(δ), cosδ = Math.cos(δ)
    const sinφ2 = sinφ1 * cosδ + cosφ1 * sinδ * Math.cos(θ)
    const φ2 = Math.asin(sinφ2)
    const y = Math.sin(θ) * sinδ * cosφ1
    const x = cosδ - sinφ1 * sinφ2
    const λ2 = λ1 + Math.atan2(y, x)
    return [ (φ2 * 180) / Math.PI, (((λ2 * 180) / Math.PI + 540) % 360) - 180 ]
  }

  const arrow = computeArrow()

  return (
    <div className="wildlife-location-section">
      <div className="location-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>📍 Where did you see the wildlife?</h3>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {/* Basemap switcher and site selector moved to header */}
          <BaseMapSwitcher value={baseId} onChange={setBaseId} disabled={locked} />
          {/* Children expected to be the site selector */}
          {children}
          <button 
            type="button" 
            className="btn use-gps-btn"
            onClick={onUseCurrentLocation}
            disabled={loading}
            title="Use my location"
            aria-label="Use my location"
          >
            ⌖
          </button>
        </div>
      </div>
      
      <MapContainer
        center={initialCenter}
        zoom={15}
        style={{ height: '60vh', width: '100%', borderRadius: '12px', position: 'relative' }}
        dragging={!locked}
        scrollWheelZoom={!locked}
        doubleClickZoom={!locked}
        ref={mapRef as any}
      >
        <TileLayer url={baseCfg.url} attribution={baseCfg.attribution} {...(baseCfg.options || {})} />
        <MapViewController position={position} />
        <CenterController />
        <MapClickHandler />
        <InteractionController />
        {position && <Marker position={[position[1], position[0]]} />}
        {locked && arrow && (
          <>
            <Polyline className="wildlife-arrow" positions={[arrow.start, arrow.end]} pathOptions={{ color: '#e74c3c', weight: 4, opacity: 1, stroke: true }} />
            <Polyline className="wildlife-arrow" positions={[arrow.end, arrow.headLeft]} pathOptions={{ color: '#e74c3c', weight: 4, opacity: 1, stroke: true }} />
            <Polyline className="wildlife-arrow" positions={[arrow.end, arrow.headRight]} pathOptions={{ color: '#e74c3c', weight: 4, opacity: 1, stroke: true }} />
          </>
        )}
        {/* No overlay cancel; use the red cross under the map header */}
        {/* Direction buttons visible only in locked state */}
        {locked && (
          <>
            {/* NW (offset below controls) */}
            <div style={{ position: 'absolute', top: 56, left: 8, zIndex: 1000 }} onMouseDown={(e)=>{e.stopPropagation();}} onTouchStart={(e)=>{e.stopPropagation();}}>
              <button type="button" title="NW (315°)" className={`btn btn-sm ${movement==='NW'?'btn-primary':''}`} onClick={(e)=>{e.preventDefault(); e.stopPropagation(); onMovementChange('NW')}}>NW</button>
            </div>
            {/* N top center */}
            <div style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 1000 }} onMouseDown={(e)=>{e.stopPropagation();}} onTouchStart={(e)=>{e.stopPropagation();}}>
              <button type="button" title="N (0°)" className={`btn btn-sm ${movement==='N'?'btn-primary':''}`} onClick={(e)=>{e.preventDefault(); e.stopPropagation(); onMovementChange('N')}}>N</button>
            </div>
            {/* NE top right */}
            <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 1000 }} onMouseDown={(e)=>{e.stopPropagation();}} onTouchStart={(e)=>{e.stopPropagation();}}>
              <button type="button" title="NE (45°)" className={`btn btn-sm ${movement==='NE'?'btn-primary':''}`} onClick={(e)=>{e.preventDefault(); e.stopPropagation(); onMovementChange('NE')}}>NE</button>
            </div>
            {/* W center left */}
            <div style={{ position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)', zIndex: 1000 }} onMouseDown={(e)=>{e.stopPropagation();}} onTouchStart={(e)=>{e.stopPropagation();}}>
              <button type="button" title="W (270°)" className={`btn btn-sm ${movement==='W'?'btn-primary':''}`} onClick={(e)=>{e.preventDefault(); e.stopPropagation(); onMovementChange('W')}}>W</button>
            </div>
            {/* Only 8 directional buttons in the map (no stationary) */}
            {/* E center right */}
            <div style={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', zIndex: 1000 }} onMouseDown={(e)=>{e.stopPropagation();}} onTouchStart={(e)=>{e.stopPropagation();}}>
              <button type="button" title="E (90°)" className={`btn btn-sm ${movement==='E'?'btn-primary':''}`} onClick={(e)=>{e.preventDefault(); e.stopPropagation(); onMovementChange('E')}}>E</button>
            </div>
            {/* SW bottom left */}
            <div style={{ position: 'absolute', bottom: 8, left: 8, zIndex: 1000 }} onMouseDown={(e)=>{e.stopPropagation();}} onTouchStart={(e)=>{e.stopPropagation();}}>
              <button type="button" title="SW (225°)" className={`btn btn-sm ${movement==='SW'?'btn-primary':''}`} onClick={(e)=>{e.preventDefault(); e.stopPropagation(); onMovementChange('SW')}}>SW</button>
            </div>
            {/* S bottom center */}
            <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 1000 }} onMouseDown={(e)=>{e.stopPropagation();}} onTouchStart={(e)=>{e.stopPropagation();}}>
              <button type="button" title="S (180°)" className={`btn btn-sm ${movement==='S'?'btn-primary':''}`} onClick={(e)=>{e.preventDefault(); e.stopPropagation(); onMovementChange('S')}}>S</button>
            </div>
            {/* SE bottom right */}
            <div style={{ position: 'absolute', bottom: 8, right: 8, zIndex: 1000 }} onMouseDown={(e)=>{e.stopPropagation();}} onTouchStart={(e)=>{e.stopPropagation();}}>
              <button type="button" title="SE (135°)" className={`btn btn-sm ${movement==='SE'?'btn-primary':''}`} onClick={(e)=>{e.preventDefault(); e.stopPropagation(); onMovementChange('SE')}}>SE</button>
            </div>
          </>
        )}
      </MapContainer>
      
      {position && (
        <div className="location-display">
          <div>
            📍 Lat: {position[1].toFixed(6)}, Lng: {position[0].toFixed(6)}
            <button 
              type="button" 
              className="clear-location" 
              onClick={() => onLocationChange(null)}
              title="Clear position"
            >
              ×
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
            <span style={{ color: '#374151' }}>
              {movement === 'Stationary' ? 'stationary' : `moving ${movement}`}
            </span>
            <button
              type="button"
              className="clear-location"
              title="Set stationary"
              onClick={() => onMovementChange('Stationary')}
              aria-pressed={movement === 'Stationary'}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function WildlifeObservation({ onClose, onSuccess }: WildlifeObservationProps) {
  const [step, setStep] = useState(1) // 1: location, 2: details
  const [position, setPosition] = useState<[number, number] | null>(null)
  const [selectedType, setSelectedType] = useState<string | null>(null)
  const [count, setCount] = useState(1)
  const [observationKind, setObservationKind] = useState<string>('observation')
  const [comment, setComment] = useState('')
  const [sites, setSites] = useState<Site[]>([])
  const [areas, setAreas] = useState<Area[]>([])
  const [selectedSite, setSelectedSite] = useState<number | null>(null)
  const [selectedArea, setSelectedArea] = useState<number | null>(null)
  const [wildlifeTypes, setWildlifeTypes] = useState<Record<string, WildlifeType>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [directionDeg, setDirectionDeg] = useState<number | null>(null)
  const [movement, setMovement] = useState<string>('Stationary')
  const [groupFemale, setGroupFemale] = useState(false)
  const [groupYoung, setGroupYoung] = useState(false)
  const [groupMale, setGroupMale] = useState(false)
  const [summary, setSummary] = useState<null | {
    speciesKey: string,
    speciesName: string,
    count: number,
    siteName: string,
    coords: [number, number],
    direction: number | null,
    notes: string,
  }>(null)
  const [gpsCenter, setGpsCenter] = useState<[number, number] | null>(null) // [lat, lng]
  const [centerTick, setCenterTick] = useState(0)
  const didInitGeoRef = useRef(false)
  // const [gpsSupported, setGpsSupported] = useState(false)
  const setGpsSupported = (_value: boolean) => {/* no-op */}

  useEffect(() => {
    fetchSites()
    fetchAllAreas()
    fetchWildlifeTypes()
    setGpsSupported('geolocation' in navigator)
    // Initial geolocation center (guarded to run once)
    if (!didInitGeoRef.current && navigator.geolocation) {
      didInitGeoRef.current = true
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords
          setGpsCenter([latitude, longitude])
          setCenterTick((t) => t + 1)
        },
        () => {}
      )
    }
  }, [])

  // When the user selects a point on the map (or GPS updates),
  // auto-select the matching site/area if within known boundaries.
  useEffect(() => {
    if (position) {
      autoSelectSiteAndArea(position)
    }
  }, [position, areas, sites])

  // Update direction when movement changes
  useEffect(() => {
    const map: Record<string, number | null> = {
      Stationary: null,
      N: 0,
      NE: 45,
      E: 90,
      SE: 135,
      S: 180,
      SW: 225,
      W: 270,
      NW: 315,
    }
    setDirectionDeg(map[movement])
  }, [movement])
  

  const fetchSites = async () => {
    try {
      // Use non-admin user endpoint for accessible sites
      const response = await fetchWithAuth('/api/sites')
      if (response.ok) {
        const data = await response.json()
        setSites(data)
        if (data.length === 1) {
          setSelectedSite(data[0].id) // Auto-select if only one site
        }
      }
    } catch (err) {
      console.error('Failed to fetch sites:', err)
    }
  }

  const fetchAllAreas = async () => {
    try {
      // First get all accessible sites, then fetch areas for each
      const sitesResponse = await fetchWithAuth('/api/sites')
      if (sitesResponse.ok) {
        const sites = await sitesResponse.json()
        const allAreas: Area[] = []
        
        // Fetch areas for each site
        for (const site of sites) {
          try {
            const areasResponse = await fetchWithAuth(`/api/sites/${site.id}/areas`)
            if (areasResponse.ok) {
              const siteAreas = await areasResponse.json()
              allAreas.push(...siteAreas)
            }
          } catch (err) {
            console.error(`Failed to fetch areas for site ${site.id}:`, err)
          }
        }
        
        setAreas(allAreas)
      }
    } catch (err) {
      console.error('Failed to fetch areas:', err)
    }
  }

  const fetchWildlifeTypes = async () => {
    try {
      const response = await fetchWithAuth('/api/assets/wildlife-types')
      if (response.ok) {
        const data = await response.json()
        setWildlifeTypes(data)
      }
    } catch (err) {
      console.error('Failed to fetch wildlife types:', err)
    }
  }

  const autoSelectSiteAndArea = (gpsPosition: [number, number]) => {
    // First, try to find an area that contains the GPS position
    const matchingArea = areas.find(area => {
      if (area.geometry_json && isPointInMultiPolygon(gpsPosition, area.geometry_json)) return true
      if (area.polygon_coords && area.polygon_coords.length > 0) return isPointInPolygon(gpsPosition, area.polygon_coords as [number, number][])
      return false
    })

    if (matchingArea) {
      setSelectedSite(matchingArea.site_id)
      setSelectedArea(matchingArea.id)
            return
    }

    // If no area matches, try to find a site that contains the GPS position
    const matchingSite = sites.find(site => {
      if (site.geometry_json && isPointInMultiPolygon(gpsPosition, site.geometry_json)) return true
      if (site.polygon_coords && site.polygon_coords.length > 0) return isPointInPolygon(gpsPosition, site.polygon_coords as [number, number][])
      return false
    })

    if (matchingSite) {
      setSelectedSite(matchingSite.id)
      setSelectedArea(null) // Clear area selection if only site matches
            return
    }
    // Otherwise, select the nearest site by polygon centroid
    let nearest: { id: number; dist: number } | null = null
    for (const s of sites) {
      let centroid: [number, number] | null = null
      if (s.geometry_json) centroid = centroidOfMultiPolygon(s.geometry_json)
      if (!centroid && s.polygon_coords && s.polygon_coords.length > 0) centroid = centroidOfPolygon(s.polygon_coords as [number, number][])
      if (!centroid) continue
      const d = distanceMeters(gpsPosition[1], gpsPosition[0], centroid[0], centroid[1])
      if (!nearest || d < nearest.dist) nearest = { id: s.id, dist: d }
    }
    if (nearest) {
      setSelectedSite(nearest.id)
      setSelectedArea(null)
    }
  }

  function centroidOfPolygon(poly: [number, number][]): [number, number] {
    // poly is [lat,lng]; return [lat,lng]
    let x = 0, y = 0
    for (const [lat, lng] of poly) { x += lat; y += lng }
    const n = poly.length || 1
    return [x / n, y / n]
  }
  function distanceMeters(lat1: number, lon1: number, lat2: number, lon2: number) {
    const R = 6371000
    const toRad = (d: number) => (d * Math.PI) / 180
    const dLat = toRad(lat2 - lat1)
    const dLon = toRad(lon2 - lon1)
    const a = Math.sin(dLat/2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon/2) ** 2
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  // Preselect a site at step 1 if none chosen yet (use selected position if set; otherwise nearest to GPS center)
  useEffect(() => {
    if (step !== 1 || selectedSite != null || sites.length === 0) return
    if (sites.length === 1) { setSelectedSite(sites[0].id); return }
    let nearest: { id: number; dist: number } | null = null
    for (const s of sites) {
      let cent: [number, number] | null = null
      if (s.geometry_json) cent = centroidOfMultiPolygon(s.geometry_json)
      if (!cent && s.polygon_coords && s.polygon_coords.length > 0) cent = centroidOfPolygon(s.polygon_coords as any)
      if (!cent) continue
      const d = position
        ? distanceMeters(position[1], position[0], cent[0], cent[1])
        : gpsCenter
          ? distanceMeters(gpsCenter[0], gpsCenter[1], cent[0], cent[1])
          : Number.POSITIVE_INFINITY
      if (!nearest || d < nearest.dist) nearest = { id: s.id, dist: d }
    }
    if (nearest) setSelectedSite(nearest.id)
  }, [step, selectedSite, sites, position, gpsCenter])

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser')
      return
    }

    setLoading(true)
    setError(null) // Clear any previous errors
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        // Center map on user location without selecting wildlife position
        setGpsCenter([latitude, longitude])
        setCenterTick((t) => t + 1)
        setLoading(false)
        setError(null) // Clear any errors on success
      },
      (error) => {
        setLoading(false)
        let errorMessage = 'Unable to get your location: '
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Location access denied by user. Please enable location access in your browser settings.'
            break
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Location information unavailable.'
            break
          case error.TIMEOUT:
            errorMessage += 'Location request timed out. Please try again.'
            break
          default:
            errorMessage += error.message
            break
        }
        
        setError(errorMessage)
      },
      {
        enableHighAccuracy: false, // Try with less accuracy first
        timeout: 10000, // Shorter timeout for testing
        maximumAge: 300000 // Accept cached position up to 5 minutes old
      }
    )
  }

  const handleSubmit = async () => {
    if (!position || !selectedType) {
      setError('Please set location and select wildlife type')
      return
    }
    // Fallback to single or nearest site if not explicitly selected
    let siteForSubmit = selectedSite
    if (siteForSubmit == null) {
      if (sites.length === 1) siteForSubmit = sites[0].id
      else {
        let nearest: { id: number; dist: number } | null = null
        for (const s of sites) {
          let cent: [number, number] | null = null
          const c1 = s.geometry_json ? centroidOfMultiPolygon(s.geometry_json) : null
          if (c1) cent = c1
          if (!cent && s.polygon_coords && s.polygon_coords.length > 0) cent = centroidOfPolygon(s.polygon_coords as any)
          if (!cent) continue
          const d = distanceMeters(position[1], position[0], cent[0], cent[1])
          if (!nearest || d < nearest.dist) nearest = { id: s.id, dist: d }
        }
        if (nearest) siteForSubmit = nearest.id
      }
    }
    if (siteForSubmit == null) {
      setError('Please select a site')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Dedicated fields now handle observation type and group flags

      const response = await fetchWithAuth('/api/assets/wildlife-observation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subcategory: selectedType,
          count,
          comment: (comment.trim()).trim() || null,
          site_id: siteForSubmit,
          area_id: selectedArea || null,
          position_coords: position,
          direction_deg: directionDeg == null ? undefined : directionDeg,
          observation_type: observationKind,
          group_female: groupFemale,
          group_young: groupYoung,
          group_male: groupMale,
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to record wildlife observation')
      }

      // Go to step 3 (summary) without closing
      const species = wildlifeTypes[selectedType]
      setSummary({
        speciesKey: selectedType,
        speciesName: species?.name || selectedType,
        count,
        siteName: (sites.find(s => s.id === siteForSubmit)?.name) || 'Unknown',
        coords: [position[1], position[0]],
        direction: directionDeg,
        notes: (comment.trim()).trim(),
      })
      setStep(3)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record wildlife observation')
    } finally {
      setLoading(false)
    }
  }

  const canProceed = () => {
    switch (step) {
      case 1: return position != null && selectedSite != null
      case 2: return selectedType !== null
      default: return false
    }
  }

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="wildlife-step">
            <LocationPicker
              position={position}
              onLocationChange={setPosition}
              onUseCurrentLocation={useCurrentLocation}
              loading={loading}
              movement={movement}
              onMovementChange={setMovement}
              directionDeg={directionDeg}
              locked={Boolean(position)}
              centerLatLng={gpsCenter}
              centerTick={centerTick}
            >
              {sites.length > 1 && (
                <select
                  aria-label="Site selector"
                  value={selectedSite ?? ''}
                  onChange={e => setSelectedSite(e.target.value ? Number(e.target.value) : null)}
                  style={{ padding: '0.25rem 0.5rem' }}
                  disabled={Boolean(position)}
                >
                  <option value="">Site…</option>
                  {sites.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              )}
            </LocationPicker>
            {/* Movement controls moved into the map overlay */}
          </div>
        )

      case 2:
        return (
          <div className="wildlife-step">
            <div className="wildlife-section">
              <div className="kind-segmented" role="tablist" aria-label="Observation type">
                {[
                  { key: 'observation', label: 'observation' },
                  { key: 'wounded', label: 'Wounded game' },
                  { key: 'downed', label: 'Downed Game' },
                  { key: 'tracks', label: 'Tracks' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    role="tab"
                    aria-selected={observationKind === opt.key}
                    className={`kind-segment ${observationKind === opt.key ? 'active' : ''}`}
                    onClick={() => setObservationKind(opt.key)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <h3 style={{ margin: 0, marginBottom: '0.5rem' }}>🦌 What did you see?</h3>
            <div className="wildlife-grid">
              {['moose','red_deer','fallow_deer','roe_deer','wild_boar','red_fox','badger']
                .filter(k => wildlifeTypes[k])
                .map((key) => {
                  const type = wildlifeTypes[key]
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`wildlife-type-btn ${selectedType === key ? 'selected' : ''}`}
                      onClick={() => setSelectedType(key)}
                      style={{ 
                        backgroundColor: selectedType === key ? type.color : 'transparent',
                        color: selectedType === key ? 'white' : '#111827',
                        border: selectedType === key ? '1px solid transparent' : '1px solid #e5e7eb',
                        padding: '8px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <div className="wildlife-icon" style={{ fontSize: 18, lineHeight: 1 }}>{type.icon}</div>
                      <div className="wildlife-name" style={{ fontSize: 14 }}>{type.name}</div>
                    </button>
                  )
                })}
              {(() => {
                const otherKeys = ['hare','wolf','mufflon','lynx','bear','beaver','wolverine']
                const isOtherSelected = otherKeys.includes(selectedType || '')
                const selColor = isOtherSelected && selectedType ? (wildlifeTypes[selectedType]?.color || '#6b7280') : undefined
                return (
                  <div
                    key="other"
                    className={`wildlife-type-btn other-tile ${isOtherSelected ? 'selected' : ''}`}
                    style={{
                      backgroundColor: isOtherSelected ? selColor : 'transparent',
                      color: isOtherSelected ? 'white' : '#111827',
                      border: isOtherSelected ? '1px solid transparent' : '1px solid #e5e7eb',
                      padding: '8px 12px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className="wildlife-icon" style={{ fontSize: 18, lineHeight: 1 }}>🐾</div>
                      <div className="wildlife-name" style={{ fontSize: 14 }}>Other</div>
                    </div>
                    <select
                      aria-label="Other species"
                      className="other-select"
                      value={isOtherSelected ? (selectedType as string) : ''}
                      onChange={(e) => setSelectedType(e.target.value || null)}
                    >
                      <option value="">Select other…</option>
                      {otherKeys
                        .filter(k => wildlifeTypes[k])
                        .map(k => (
                          <option key={k} value={k}>{wildlifeTypes[k].name}</option>
                        ))}
                    </select>
                  </div>
                )
              })()}
            </div>
            <div className="count-section" style={{ marginTop: '0.75rem' }}>
              <label>Number of animals:</label>
              <div className="count-controls">
                <button type="button" className="count-btn" onClick={() => setCount(Math.max(1, count - 1))} disabled={count <= 1}>−</button>
                <span className="count-display">{count}</span>
                <button type="button" className="count-btn" onClick={() => setCount(count + 1)}>+</button>
              </div>
            </div>
            <div className="group-toggle-row" style={{ marginTop: '0.5rem' }}>
              <div className="group-toggle-label">Group diversity</div>
              <button
                type="button"
                className={`group-toggle ${groupFemale ? 'active' : ''}`}
                aria-pressed={groupFemale}
                aria-label="Toggle group includes Female"
                onClick={() => setGroupFemale(v => !v)}
              >
                include females
              </button>
              <button
                type="button"
                className={`group-toggle ${groupYoung ? 'active' : ''}`}
                aria-pressed={groupYoung}
                aria-label="Toggle group includes Young"
                onClick={() => setGroupYoung(v => !v)}
              >
                include young
              </button>
              <button
                type="button"
                className={`group-toggle ${groupMale ? 'active' : ''}`}
                aria-pressed={groupMale}
                aria-label="Toggle group includes Male"
                onClick={() => setGroupMale(v => !v)}
              >
                include males
              </button>
            </div>
            <div className="comment-section" style={{ marginTop: '0.75rem' }}>
              <label>Additional notes (optional):</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Behavior, condition, etc."
                rows={3}
                maxLength={500}
              />
              <small>{comment.length}/500 characters</small>
            </div>
          </div>
        )

      case 3:
        return (
            <div className="wildlife-step">
              <h3>✅ Observation Recorded</h3>
              <div className="confirmation-details">
                <div className="detail-row"><span className="detail-label">Wildlife:</span> <span className="detail-value">{summary?.count} {summary?.speciesName}</span></div>
                <div className="detail-row"><span className="detail-label">Observation Type:</span> <span className="detail-value">{(() => {
                  const map: Record<string,string> = { observation: 'observation', wounded: 'Wounded game', downed: 'Downed Game', tracks: 'Tracks' }
                  return map[observationKind] || observationKind
                })()}</span></div>
                {(groupFemale || groupYoung || groupMale) && (
                  <div className="detail-row"><span className="detail-label">Group:</span> <span className="detail-value">{[
                    groupFemale ? 'Female' : null,
                    groupYoung ? 'Young' : null,
                    groupMale ? 'Male' : null,
                  ].filter(Boolean).join(', ')}</span></div>
                )}
                <div className="detail-row"><span className="detail-label">Site:</span> <span className="detail-value">{summary?.siteName}</span></div>
              <div className="detail-row"><span className="detail-label">Coordinates:</span> <span className="detail-value">{summary ? `${summary.coords[0].toFixed(6)}, ${summary.coords[1].toFixed(6)}` : '-'}</span></div>
              <div className="detail-row"><span className="detail-label">Direction:</span> <span className="detail-value">{summary?.direction == null ? 'Stationary' : `${summary?.direction}°`}</span></div>
              {summary?.notes && (
                <div className="detail-row"><span className="detail-label">Notes:</span> <span className="detail-value">{summary?.notes}</span></div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                className="btn btn-primary"
                onClick={() => {
                  // reset to a new observation
                  setStep(1)
                  setPosition(null)
                  setSelectedType(null)
                  setCount(1)
                  setComment('')
                  setSelectedArea(null)
                  setMovement('Stationary')
                  setObservationKind('observation')
                  setGroupFemale(false); setGroupYoung(false); setGroupMale(false)
                  setSummary(null)
                }}
              >
                Make another observation
              </button>
              <button className="btn" onClick={onSuccess}>Quit to dashboard</button>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="wildlife-overlay">
      <div className="wildlife-container">
        <div className="wildlife-header">
          <h2>🦌 Wildlife Observation</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="wildlife-progress">
          {[1, 2, 3].map(i => (
            <div 
              key={i} 
              className={`progress-dot ${step >= i ? 'active' : ''} ${step === i ? 'current' : ''}`}
            />
          ))}
        </div>

        {error && step !== 3 && <div className="error">{error}</div>}

        <div className="wildlife-content">{renderStep()}</div>

        {step !== 3 && (
          <div className="wildlife-actions">
            {step > 1 && (
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setStep(step - 1)}
              >
                ← Back
              </button>
            )}
            {step < 2 ? (
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={() => setStep(step + 1)}
                disabled={!canProceed() || loading}
              >
                {loading ? 'Loading...' : 'Next →'}
              </button>
            ) : (
              <button 
                type="button" 
                className="btn btn-success"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Recording...' : '✅ Record Observation'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default WildlifeObservation
